import type { CSSProperties, ReactNode } from 'react'
import { Icon } from './Icon'

interface FilterChipProps {
  label: ReactNode
  active?: boolean
  onClick?: () => void
  style?: CSSProperties
  /**
   * Visual kind. `tab` (default) is the existing pill — used for navigation
   * filter chips. `toggle` reads as a switch with a check-box icon prefix —
   * used for boolean visibility toggles in the calendar toolbar.
   */
  kind?: 'tab' | 'toggle'
}

/**
 * Pill-shaped filter chip.
 *
 * `kind="tab"` (default): ink-filled when active, outline when inactive.
 * Used for navigation/filter tabs across the app — backwards-compatible.
 *
 * `kind="toggle"`: shows a checkbox icon prefix that reads as a switch.
 * Active state highlights with the terracotta accent on a neutral pill.
 * Used for boolean visibility toggles (e.g. the calendar toolbar).
 *
 * Keyboard accessible: Tab to focus, Enter/Space to activate.
 * 44px minimum hit target.
 */
export function FilterChip({
  label,
  active = false,
  onClick,
  style,
  kind = 'tab',
}: FilterChipProps): JSX.Element {
  const isToggle = kind === 'toggle'

  const baseStyle: CSSProperties = {
    fontFamily: 'inherit',
    fontSize: 11,
    padding: '3px 10px',
    borderRadius: 999,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    minHeight: 44,
    minWidth: 44,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition:
      'background 80ms ease-out, border-color 80ms ease-out, color 80ms ease-out',
  }

  const tabStyle: CSSProperties = {
    border: `1px solid ${active ? 'var(--text)' : 'var(--border)'}`,
    background: active ? 'var(--text)' : 'transparent',
    color: active ? 'var(--surface)' : 'var(--text-2)',
  }

  const toggleStyle: CSSProperties = {
    border: `1px solid ${active ? 'var(--primary-line)' : 'var(--line-strong)'}`,
    background: active ? 'var(--primary-bg)' : 'var(--surface)',
    color: active ? 'var(--primary-fg)' : 'var(--text-2)',
  }

  return (
    <button
      type="button"
      data-testid="filter-chip"
      data-active={active ? 'true' : 'false'}
      data-kind={kind}
      aria-pressed={active}
      onClick={onClick}
      style={{
        ...baseStyle,
        ...(isToggle ? toggleStyle : tabStyle),
        ...style,
      }}
    >
      {isToggle ? (
        <Icon
          name={active ? 'check_box' : 'check_box_outline_blank'}
          size={16}
          color={active ? 'var(--steel-blue)' : 'var(--text-3)'}
          aria-hidden
        />
      ) : null}
      {label}
    </button>
  )
}
